<template>
    <div class="issuer-overview" :class="{ 'issuer-overview--compact': !hasIssuerDetails }">
        <section v-if="showSubscription" class="issuer-overview__subscription">
            <div class="issuer-overview__subscription-row">
                <span>{{ data.subscription.label }}</span>
                <strong>{{ data.subscription.availableUnits }}</strong>
            </div>
            <div
                class="issuer-overview__progress"
                role="progressbar"
                :aria-label="`${data.subscription.soldLabel}; ${data.subscription.availableUnits}`"
                :aria-valuenow="data.subscription.percent"
                aria-valuemin="0"
                aria-valuemax="100"
            >
                <span :style="{ width: `${data.subscription.percent}%` }"></span>
            </div>
            <p class="issuer-overview__sold-label">{{ data.subscription.soldLabel }}</p>
        </section>

        <div class="issuer-overview__yield-grid" role="list" aria-label="Yield comparison">
            <div v-for="item in data.yieldHighlights" :key="item.label" class="issuer-overview__yield-card" role="listitem">
                <strong :class="{ 'issuer-overview__yield-card-value--blue': item.label === 'Clean / dirty price' }">{{ item.value }}</strong>
                <span>{{ item.label }}</span>
                <p>{{ item.description }}</p>
            </div>
        </div>

        <section
            v-if="hasIssuerDetails"
            class="issuer-overview__about"
        >
            <div v-if="data.issuerSummary" class="issuer-overview__about-copy">
                <h2 id="about-issuer-title" class="issuer-overview__about-title">{{ data.issuerSummaryTitle }}</h2>
                <p>{{ data.issuerSummary }}</p>
            </div>
            <dl v-if="data.issuerFacts.length" class="issuer-overview__fact-list">
                <div v-for="fact in data.issuerFacts" :key="fact.label" class="issuer-overview__fact-row">
                    <dt>{{ fact.label }}</dt>
                    <dd>{{ fact.value }}</dd>
                </div>
            </dl>
            <div v-if="data.whyThisBond.length" class="issuer-overview__why">
                <h3 class="issuer-overview__why-title">{{ data.whyThisBondTitle }}</h3>
                <ul>
                    <li v-for="reason in data.whyThisBond" :key="reason">{{ reason }}</li>
                </ul>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useBondV2Data } from './bondV2Context'

interface Props {
    showSubscription?: boolean
}

withDefaults(defineProps<Props>(), {
    showSubscription: true
})

const data = useBondV2Data()
const hasIssuerDetails = computed(() => Boolean(
    data.value.issuerSummary ||
    data.value.issuerFacts.length ||
    data.value.whyThisBond.length
))
</script>

<style scoped>
.issuer-overview {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    justify-content: flex-start;
    width: 100%;
    min-width: 0;
}

.issuer-overview--compact {
    max-width: none;
    margin: 0;
}

.issuer-overview__subscription,
.issuer-overview__yield-card {
    border: none;
    border-radius: 7px;
    background: #fff;
}

.issuer-overview__about {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 18px;
    background: #F9FBFF;
    border-radius: 0;
    min-width: 0;
    padding: 8px 0 0;
    box-sizing: border-box;
}

.issuer-overview__subscription {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    padding: 18px 20px;
    min-width: 0;
    box-sizing: border-box;
}

.issuer-overview__subscription-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    color: #5b6684;
    font-family: Inter, var(--font-family-primary);
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    min-width: 0;
}

.issuer-overview__subscription-row strong {
    color: var(--bond-v2-text, var(--color-text-primary));
    font-size: 15px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
    flex: 0 0 auto;
}

.issuer-overview__progress {
    height: 6px;
    background: #eaf7ff;
    border-radius: 999px;
    overflow: hidden;
    margin-top: 14px;
}

.issuer-overview__progress span {
    display: block;
    height: 100%;
    background: var(--bond-v2-primary-dark, var(--color-primary-30));
}

.issuer-overview__sold-label {
    text-align: right;
    margin: 8px 0 0;
    color: var(--bond-v2-text, var(--color-text-primary));
    font-size: 15px;
    font-style: normal;
    font-weight: 600;
}

.issuer-overview__yield-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    align-items: stretch;
    justify-content: stretch;
    width: 100%;
    min-width: 0;
    padding: 0;
    box-sizing: border-box;
}

.issuer-overview__yield-card {
    padding: 16px 20px;
    min-height: 84px;
    box-sizing: border-box;
}

.issuer-overview__yield-card strong {
    display: block;
    color: #067a49;
    font-size: 15px;
    font-weight: 900;
    line-height: 21px;
}

.issuer-overview__yield-card strong.issuer-overview__yield-card-value--blue {
    color: #205aeb;
}

.issuer-overview__yield-card span {
    display: block;
    margin-top: 5px;
    color: #1b2559;
    font-family: Inter, var(--font-family-primary);
    font-size: 13px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px;
    letter-spacing: 0;
}

.issuer-overview__yield-card p {
    margin: 3px 0 0;
    color: #5b6684;
    font-family: "Public Sans", var(--font-family-primary);
    font-size: 11px;
    font-style: normal;
    font-weight: 400;
    line-height: 11px;
    letter-spacing: 0;
}

.issuer-overview__about-copy,
.issuer-overview__why {
    min-width: 0;
}

.issuer-overview__about-title,
.issuer-overview__why-title {
    margin: 0;
    color: var(--bond-v2-text, var(--color-text-primary));
    font-size: 14px;
    line-height: 1.4;
    font-weight: 800;
}

.issuer-overview__about-copy p {
    margin: 10px 0 0;
    color: #5b6684;
    font-size: 13px;
    line-height: 1.6;
    font-weight: 500;
    overflow-wrap: anywhere;
}

.issuer-overview__fact-list {
    border: 1px solid var(--bond-v2-border, var(--color-border-light));
    border-radius: 7px;
    background: #fff;
    overflow: hidden;
    margin: 0;
    padding: 0;
    min-width: 0;
}

.issuer-overview__fact-row {
    display: grid;
    grid-template-columns: minmax(180px, 0.35fr) minmax(0, 1fr);
    align-items: center;
    gap: 20px;
    min-height: 42px;
    padding: 0 10px;
    background: #fff;
    min-width: 0;
}

.issuer-overview__fact-row + .issuer-overview__fact-row {
    border-top: none;
    margin-top: 0;
}

.issuer-overview__fact-row dt,
.issuer-overview__fact-row dd {
    margin: 0;
    color: #1b2559;
    font-size: 13px;
    font-weight: 500;
    min-width: 0;
    overflow-wrap: anywhere;
}

.issuer-overview__fact-row dt {
    color: #1b2559;
}

.issuer-overview__fact-row dd {
    text-align: right;
}

.issuer-overview__why ul {
    margin: 10px 0 0;
    padding-left: 18px;
}

.issuer-overview__why li {
    color: #5b6684;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.7;
    overflow-wrap: anywhere;
}

@media (max-width: 1180px) {
    .issuer-overview__about {
        gap: 14px;
    }
}

@media (max-width: 640px) {
    .issuer-overview__yield-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
    }

    .issuer-overview__subscription {
        padding: 14px 12px;
    }

    .issuer-overview__subscription-row,
    .issuer-overview__subscription-row strong,
    .issuer-overview__sold-label {
        font-size: 13px;
        line-height: 18px;
    }

    .issuer-overview__yield-card {
        min-height: 76px;
        padding: 12px;
    }

    .issuer-overview__yield-card strong {
        font-size: 14px;
        line-height: 18px;
    }

    .issuer-overview__yield-card span {
        font-size: 11px;
        line-height: 16px;
        letter-spacing: 0;
    }

    .issuer-overview__yield-card p {
        font-size: 10px;
        line-height: 13px;
        letter-spacing: 0;
    }

    .issuer-overview__about-title,
    .issuer-overview__why-title {
        font-size: 13px;
    }

    .issuer-overview__about-copy p,
    .issuer-overview__why li,
    .issuer-overview__fact-row dt,
    .issuer-overview__fact-row dd {
        font-size: 12px;
    }

    .issuer-overview__fact-row {
        grid-template-columns: minmax(0, 1fr);
        gap: 4px;
    }

    .issuer-overview__fact-row dd {
        text-align: left;
    }
}
</style>
